import React, { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { useTheme } from "../lib/theme";

const SUITS = [
  {
    id: "classic",
    name: "Classic Red",
    tag: "The web-slinger",
    desc: "Bold red-and-navy Spider-Man energy. The default hero.",
    bg: "linear-gradient(135deg, #061638 0%, #0A1D4F 45%, #7C1218 100%)",
    accent: "#DC1F26",
    keyframe: "picker-web-ripple",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <g stroke="#DC1F26" strokeWidth="0.9" opacity="0.9">
          <circle cx="100" cy="100" r="24" />
          <circle cx="100" cy="100" r="46" />
          <circle cx="100" cy="100" r="72" />
          <circle cx="100" cy="100" r="100" />
          <line x1="100" y1="6" x2="100" y2="194" />
          <line x1="6" y1="100" x2="194" y2="100" />
          <line x1="30" y1="30" x2="170" y2="170" />
          <line x1="170" y1="30" x2="30" y2="170" />
        </g>
      </svg>
    ),
  },
  {
    id: "symbiote",
    name: "Symbiote Black",
    tag: "Feel the bond",
    desc: "Stealth-mode near-black with high-contrast whites.",
    bg: "linear-gradient(135deg, #050505 0%, #1F1F1F 45%, #3F3F46 100%)",
    accent: "#F1F5F9",
    keyframe: "picker-ink-drip",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="pick-ink1" cx="30%" cy="0%" r="60%">
            <stop offset="0%" stopColor="#0A0A0A" stopOpacity="1" />
            <stop offset="70%" stopColor="#0A0A0A" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="pick-ink2" cx="70%" cy="10%" r="55%">
            <stop offset="0%" stopColor="#0A0A0A" stopOpacity="1" />
            <stop offset="70%" stopColor="#0A0A0A" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="200" height="200" fill="url(#pick-ink1)" />
        <rect width="200" height="200" fill="url(#pick-ink2)" />
        <g stroke="rgba(255,255,255,0.45)" strokeWidth="0.6">
          <path d="M20 40 Q 60 60 100 45 T 180 60" fill="none" />
          <path d="M30 90 Q 70 110 110 95 T 175 105" fill="none" />
          <path d="M25 140 Q 65 160 105 145 T 180 155" fill="none" />
        </g>
      </svg>
    ),
  },
  {
    id: "miles",
    name: "Miles Morales",
    tag: "Brooklyn stitched",
    desc: "Deep indigo-purple with electric crimson highlights.",
    bg: "linear-gradient(135deg, #0B0620 0%, #1E1B4B 45%, #7C0F32 100%)",
    accent: "#E11D48",
    keyframe: "picker-electric",
    svg: (
      <svg viewBox="0 0 200 200" fill="none" className="absolute inset-0 w-full h-full">
        <g stroke="#A855F7" strokeWidth="1.5" strokeLinecap="round" opacity="0.95">
          <path d="M15 100 Q 50 75 75 95 T 125 85 T 175 100 T 195 90" fill="none" />
          <path d="M20 130 Q 55 110 85 125 T 140 115 T 190 130" fill="none" />
          <path d="M25 70 Q 60 55 90 75 T 135 60 T 190 70" fill="none" />
        </g>
        <g stroke="#E11D48" strokeWidth="1.1" strokeLinecap="round" opacity="0.85">
          <path d="M20 105 Q 60 85 90 105 T 140 90 T 185 100" fill="none" />
        </g>
      </svg>
    ),
  },
];

export default function SuitPicker({ userName, onDone }) {
  const { setSuit } = useTheme();
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);

  const pick = (id) => {
    if (locked) return;
    setSelected(id);
    // Fire the suit change immediately so the user sees the signature flash overlay
    setSuit(id);
    setLocked(true);
    // Give the intro animation ~900ms of the spotlight, then continue to the app
    setTimeout(() => onDone?.(), 900);
  };

  const skip = () => {
    if (locked) return;
    setLocked(true);
    onDone?.();
  };

  return (
    <div
      data-testid="suit-picker"
      className="fixed inset-0 z-[9000] bg-slate-950/85 backdrop-blur-md grid place-items-center px-4 py-6 sm:py-10 animate-in fade-in duration-300"
    >
      <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-10 shadow-2xl shadow-black/40 border border-slate-200 dark:border-slate-800 max-h-[92vh] overflow-y-auto">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400 mb-4">
            One quick step
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">
            Welcome{userName ? `, ${userName.split(" ")[0]}` : ""} — pick your suit
          </h2>
          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
            Your suit changes every hero, button, and animation across Argent. You can swap any time from Settings.
          </p>
        </div>

        <div className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {SUITS.map((s) => {
            const isPicked = selected === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => pick(s.id)}
                disabled={locked}
                aria-label={`Choose ${s.name}`}
                data-testid={`suit-pick-${s.id}`}
                className={`relative rounded-3xl overflow-hidden border-2 transition-all duration-300 aspect-[4/5] group text-left ${
                  isPicked
                    ? "border-white shadow-2xl shadow-black/50 scale-[1.04]"
                    : locked
                    ? "border-slate-200 dark:border-slate-800 opacity-40"
                    : "border-slate-200 dark:border-slate-800 hover:border-slate-500 dark:hover:border-slate-500 hover:scale-[1.02] cursor-pointer"
                }`}
                style={{ background: s.bg }}
              >
                <div
                  className="absolute inset-0 opacity-90"
                  style={{ animation: `${s.keyframe} 2400ms ease-in-out infinite` }}
                >
                  {s.svg}
                </div>
                {isPicked && (
                  <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white grid place-items-center shadow-lg animate-in zoom-in duration-200">
                    <Check className="w-4 h-4 text-slate-900" />
                  </div>
                )}
                <div className="absolute inset-x-4 bottom-4 text-left">
                  <div className="text-lg sm:text-xl font-display font-bold text-white" style={{ textShadow: "0 2px 4px rgba(0,0,0,0.7)" }}>
                    {s.name}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest font-bold text-white/80 mt-0.5" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.7)" }}>
                    {s.tag}
                  </div>
                  <div className="text-xs text-white/85 mt-2 leading-relaxed" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}>
                    {s.desc}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={skip}
            disabled={locked}
            className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors disabled:opacity-50"
            data-testid="suit-pick-skip"
          >
            Skip for now — take me straight in <ArrowRight className="w-3 h-3 inline-block ml-0.5" />
          </button>
        </div>

        <style>{`
          @keyframes picker-web-ripple {
            0%, 100% { transform: scale(0.95); opacity: 0.7; }
            50%      { transform: scale(1.1);  opacity: 1; }
          }
          @keyframes picker-ink-drip {
            0%, 100% { transform: translateY(-5%); opacity: 0.75; }
            50%      { transform: translateY(6%);  opacity: 1; }
          }
          @keyframes picker-electric {
            0%, 100% { opacity: 0.95; filter: drop-shadow(0 0 4px rgba(168,85,247,0.55)); }
            30%      { opacity: 0.5;  filter: drop-shadow(0 0 2px rgba(168,85,247,0.25)); }
            60%      { opacity: 1;    filter: drop-shadow(0 0 10px rgba(168,85,247,0.9)); }
            80%      { opacity: 0.65; filter: drop-shadow(0 0 3px rgba(168,85,247,0.35)); }
          }
        `}</style>
      </div>
    </div>
  );
}
